import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../components/AppIcon';
import { listScheduledPosts, cancelScheduledPost } from '../../services/scheduleService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';

/** Inline close (×) icon — avoids conflict with <Icon name="X" /> which renders the X platform logo */
const CloseIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', color: 'text-[#A78BFA]', bg: 'bg-[#A78BFA]/10', icon: 'Clock' },
  publishing: { label: 'Publishing...', color: 'text-amber-400', bg: 'bg-amber-400/10', icon: 'Loader2' },
  published: { label: 'Published', color: 'text-emerald-400', bg: 'bg-emerald-400/10', icon: 'CheckCircle' },
  failed: { label: 'Failed', color: 'text-red-400', bg: 'bg-red-400/10', icon: 'AlertCircle' },
  cancelled: { label: 'Cancelled', color: 'text-muted-foreground', bg: 'bg-muted', icon: 'XCircle' },
};

const SchedulePage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const toast = useToast();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('scheduled');
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    if (!authLoading && user) loadPosts();
  }, [user, authLoading]);

  const loadPosts = async () => {
    try {
      const result = await listScheduledPosts();
      setPosts(result?.data || []);
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (postId, e) => {
    e?.stopPropagation();
    try {
      await cancelScheduledPost(postId);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, status: 'cancelled' } : p));
      if (selectedPost?.id === postId) setSelectedPost(prev => ({ ...prev, status: 'cancelled' }));
      toast.success('Scheduled post cancelled.');
    } catch {
      toast.error('Failed to cancel. Please try again.');
    }
  };

  const tabs = [
    { key: 'scheduled', label: 'Upcoming', icon: 'Clock' },
    { key: 'published', label: 'Published', icon: 'CheckCircle' },
    { key: 'cancelled', label: 'Cancelled', icon: 'XCircle' },
  ];

  const filteredPosts = posts.filter(p => {
    if (activeTab === 'scheduled') return p.status === 'scheduled' || p.status === 'publishing';
    return p.status === activeTab;
  });

  return (
    <div className="min-h-screen bg-background">
      <main className="px-4 md:px-6 lg:px-8 pb-8 flex flex-col min-h-[calc(100vh-64px)]">
        <div className="max-w-5xl mx-auto w-full flex-1 flex flex-col justify-center">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-foreground">Scheduled Posts</h1>
              <p className="text-sm text-muted-foreground mt-1">Manage your scheduled and past posts</p>
            </div>
            <button
              onClick={() => navigate('/idea-refiner', { state: { newSession: true } })}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-all"
            >
              <Icon name="Plus" size={16} />
              New Post
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-6 border-b border-border">
            {tabs.map(tab => {
              const count = posts.filter(p => {
                if (tab.key === 'scheduled') return p.status === 'scheduled' || p.status === 'publishing';
                return p.status === tab.key;
              }).length;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-[#A78BFA] text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab.icon} size={14} />
                  {tab.label}
                  {count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      activeTab === tab.key ? 'bg-[#A78BFA]/20 text-[#A78BFA]' : 'bg-muted text-muted-foreground'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Posts grid */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="Calendar" size={40} className="mx-auto mb-4 text-muted-foreground/30" />
              <p className="text-muted-foreground">
                {activeTab === 'scheduled' ? 'No upcoming scheduled posts' : `No ${activeTab} posts`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPosts.map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  onSelect={() => setSelectedPost(post)}
                  onCancel={handleCancel}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedPost && (
          <PostDetailModal
            post={selectedPost}
            onClose={() => setSelectedPost(null)}
            onCancel={handleCancel}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── Post Card ─── */
const PostCard = ({ post, onSelect, onCancel }) => {
  const schedDate = new Date(post.scheduled_at);
  const platforms = post.platforms || {};
  const preview = (post.linkedin_content || post.twitter_content || '').slice(0, 100);
  const statusCfg = STATUS_CONFIG[post.status] || STATUS_CONFIG.scheduled;

  return (
    <button
      onClick={onSelect}
      className="w-full text-left p-4 bg-card border border-border rounded-xl hover:border-[#A78BFA]/40 hover:shadow-lg hover:shadow-[#A78BFA]/5 transition-all group"
    >
      {/* Top row: platforms + status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          {platforms.linkedin && <Icon name="Linkedin" size={14} className="text-[#0A66C2]" />}
          {platforms.twitter && <Icon name="X" size={14} className="text-foreground" />}
        </div>
        <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${statusCfg.color} ${statusCfg.bg}`}>
          <Icon name={statusCfg.icon} size={10} className={post.status === 'publishing' ? 'animate-spin' : ''} />
          {statusCfg.label}
        </span>
      </div>

      {/* Content preview */}
      <p className="text-sm text-foreground line-clamp-3 mb-3 leading-relaxed">{preview}{preview.length >= 100 ? '...' : ''}</p>

      {/* Bottom row: time + cancel */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Icon name="Clock" size={11} />
          {post.status === 'published' && post.published_at
            ? `Published ${new Date(post.published_at).toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${new Date(post.published_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`
            : `${schedDate.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${schedDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`
          }
        </span>
        {post.status === 'scheduled' && (
          <span
            onClick={(e) => { e.stopPropagation(); onCancel(post.id, e); }}
            className="text-xs text-muted-foreground hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
          >
            Cancel
          </span>
        )}
      </div>
    </button>
  );
};

/* ─── Detail Modal ─── */
const PostDetailModal = ({ post, onClose, onCancel }) => {
  const statusCfg = STATUS_CONFIG[post.status] || STATUS_CONFIG.scheduled;
  const schedDate = new Date(post.scheduled_at);
  const platforms = post.platforms || {};

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[2000] bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[2001] flex items-center justify-center p-4"
      >
        <div className="relative w-full max-w-2xl max-h-[85vh] bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden">

          {/* Header bar */}
          <div className={`px-6 py-4 border-b border-border flex items-center justify-between ${
            post.status === 'scheduled' ? 'bg-[#A78BFA]/5' :
            post.status === 'published' ? 'bg-emerald-500/5' :
            post.status === 'failed' ? 'bg-red-500/5' : 'bg-muted/30'
          }`}>
            <div className="flex items-center gap-3">
              <Icon name={statusCfg.icon} size={18} className={`${statusCfg.color} ${post.status === 'publishing' ? 'animate-spin' : ''}`} />
              <div>
                <span className={`text-sm font-semibold ${statusCfg.color}`}>{statusCfg.label}</span>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="Clock" size={11} />
                    {schedDate.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} at {schedDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="Globe" size={11} />
                    {post.timezone || 'UTC'}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>

          {/* Error banner */}
          {post.status === 'failed' && post.error_message && (
            <div className="px-6 py-3 border-b border-red-500/20 bg-red-500/5 flex items-start gap-2">
              <Icon name="AlertTriangle" size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-400">{post.error_message}</p>
            </div>
          )}

          {/* Published banner */}
          {post.status === 'published' && post.published_at && (
            <div className="px-6 py-2.5 border-b border-emerald-500/20 bg-emerald-500/5 flex items-center gap-2">
              <Icon name="CheckCircle" size={13} className="text-emerald-400" />
              <span className="text-xs text-emerald-400">
                Published {new Date(post.published_at).toLocaleDateString([], { month: 'short', day: 'numeric' })} at {new Date(post.published_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
              </span>
            </div>
          )}

          {/* Platforms */}
          <div className="px-6 py-3 border-b border-border flex items-center gap-2">
            <span className="text-xs text-muted-foreground mr-1">Platforms:</span>
            {platforms.linkedin && (
              <span className="flex items-center gap-1 text-xs bg-[#0A66C2]/10 text-[#0A66C2] px-2.5 py-1 rounded-lg">
                <Icon name="Linkedin" size={12} /> LinkedIn
              </span>
            )}
            {platforms.twitter && (
              <span className="flex items-center gap-1.5 text-xs bg-foreground/10 text-foreground px-2.5 py-1 rounded-lg">
                <Icon name="X" size={11} /> X
              </span>
            )}
          </div>

          {/* Content — scrollable */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {platforms.linkedin && post.linkedin_content && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Icon name="Linkedin" size={13} className="text-[#0A66C2]" />
                  <span className="text-xs font-medium text-muted-foreground">LinkedIn Content</span>
                  {post.status === 'scheduled' && (
                    <Icon name="Lock" size={10} className="text-muted-foreground/60 ml-1" />
                  )}
                </div>
                <div className="bg-muted/40 border border-border rounded-xl p-4 text-sm text-foreground whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "'Source Sans 3', sans-serif" }}>
                  {post.linkedin_content}
                </div>
              </div>
            )}

            {platforms.twitter && post.twitter_content && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Icon name="X" size={13} className="text-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">X Content</span>
                  {post.status === 'scheduled' && (
                    <Icon name="Lock" size={10} className="text-muted-foreground/60 ml-1" />
                  )}
                </div>
                <div className="bg-muted/40 border border-border rounded-xl p-4 text-sm text-foreground whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "'Source Sans 3', sans-serif" }}>
                  {post.twitter_content}
                </div>
              </div>
            )}
          </div>

          {/* Footer actions */}
          {post.status === 'scheduled' && (
            <div className="px-6 py-4 border-t border-border flex items-center justify-between">
              <button
                onClick={(e) => onCancel(post.id, e)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/15 rounded-lg transition-colors"
              >
                <Icon name="XCircle" size={14} />
                Cancel Scheduled Post
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {post.status !== 'scheduled' && (
            <div className="px-6 py-4 border-t border-border flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default SchedulePage;
